import { useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut, signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { auth, db, functions, googleProvider, ADMIN_EMAIL, WHITELIST_COLLECTION } from "../firebase";

// === CONFIG ACCESO ===
// Identificadores del subscription_type que da acceso al curso (paso 2 en binga-gestion).
// El gate matchea contra planType, subscriptionTypeName o subscriptionTypeId — agregá
// las variantes que correspondan (lo que usaste como nombre/clave en binga-gestion).
const FINANZAS_PLAN_KEYS = [
  "Finanzas Tool",
  "Finanzas Tools",
  "finanzas_tool",
  "finanzas-tool",
  "finanzas_alumno",
];

// URL del producto en WooCommerce para mostrar en "Sin acceso".
const WOO_PRODUCT_URL = "https://www.studiopasacademy.com/p/herramientas-financieras-para-emprendedores";

// === SESIÓN ===
// Días que dura la sesión antes de pedir un nuevo login (código o Google).
const SESSION_MAX_DAYS = 30;

// Callables del login por código (Cloud Functions en gestion).
const requestLoginCode = httpsCallable(functions, "requestFinanzasLoginCode");
const verifyLoginCode = httpsCallable(functions, "verifyFinanzasLoginCode");

const C = {
  copper: "#b5836d", copperDark: "#8a5d4a",
  dark: "#2d2a26", muted: "#7a756e",
  bg: "#faf8f6", card: "#fff", line: "#ebe5df",
  red: "#c0392b", redBg: "#fdf0ee",
  green: "#4a9c6d",
};

export const emailKey = (email) => (email || "").trim().toLowerCase();
export const isAdmin = (user) => emailKey(user?.email) === ADMIN_EMAIL;

function Shell({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", fontFamily: "'Libre Baskerville','Georgia',serif",
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
      <div style={{
        background: C.card, borderRadius: "16px", padding: "36px 28px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)", border: `1px solid ${C.line}`,
        maxWidth: "420px", width: "100%", textAlign: "center",
      }}>{children}</div>
    </div>
  );
}

export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  // Login por código: paso "email" o "code".
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      // Tope de sesión: si el último login real es más viejo que SESSION_MAX_DAYS,
      // cerramos sesión para forzar un nuevo login (código o Google).
      if (u) {
        const lastStr = u.metadata?.lastSignInTime;
        const lastMs = lastStr ? new Date(lastStr).getTime() : 0;
        const ageDays = lastMs ? (Date.now() - lastMs) / 86400000 : 0;
        if (lastMs && ageDays > SESSION_MAX_DAYS) {
          signOut(auth);
          setUser(null);
          setLoading(false);
          return;
        }
      }
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) { setAllowed(false); return; }
    if (isAdmin(user)) { setAllowed(true); return; }
    setChecking(true);
    // Tres caminos para tener acceso (en orden, con cortocircuito):
    // 0) Blocklist: si el admin revocó este email, no entra (corta todo)
    // 1) Suscripción activa al curso (compradores Woo)
    // 2) Email en la whitelist (regalos / beta testers / accesos manuales)
    (async () => {
      try {
        const blockSnap = await getDoc(doc(db, "finanzas_access_blocklist", emailKey(user.email)));
        if (blockSnap.exists()) { setAllowed(false); return; }

        const subQ = query(
          collection(db, "customer_subscriptions"),
          where("customerId", "==", user.uid),
          where("status", "in", ["active", "trial"])
        );
        const subSnap = await getDocs(subQ);
        const hasFinanzas = subSnap.docs.some((d) => {
          const data = d.data();
          return FINANZAS_PLAN_KEYS.includes(data.planType)
              || FINANZAS_PLAN_KEYS.includes(data.subscriptionTypeName)
              || FINANZAS_PLAN_KEYS.includes(data.subscriptionTypeId);
        });
        if (hasFinanzas) { setAllowed(true); return; }

        // Fallback: whitelist manual
        const wlSnap = await getDoc(doc(db, WHITELIST_COLLECTION, emailKey(user.email)));
        setAllowed(wlSnap.exists());
      } catch (e) {
        console.error(e); setError("No pudimos verificar tu acceso.");
      } finally {
        setChecking(false);
      }
    })();
  }, [user]);

  const handleSignIn = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      console.error(e);
      if (e.code !== "auth/popup-closed-by-user") setError("No se pudo iniciar sesión.");
    }
  };

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    const target = emailKey(email);
    if (!target || !target.includes("@")) { setError("Ingresá un email válido."); return; }
    setSending(true);
    try {
      await requestLoginCode({ email: target });
      setStep("code");
    } catch (e2) {
      console.error(e2);
      setError(e2?.message || "No pudimos enviar el código. Intentá de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    const clean = (code || "").replace(/\D/g, "");
    if (clean.length !== 6) { setError("El código tiene 6 dígitos."); return; }
    setVerifying(true);
    try {
      const res = await verifyLoginCode({ email: emailKey(email), code: clean });
      const token = res?.data?.token;
      if (!token) throw new Error("No recibimos el token de acceso.");
      await signInWithCustomToken(auth, token);
      // onAuthStateChanged toma el resto.
    } catch (e2) {
      console.error(e2);
      setError(e2?.message || "Código incorrecto. Revisá e intentá de nuevo.");
    } finally {
      setVerifying(false);
    }
  };

  const resetLogin = () => { setStep("email"); setCode(""); setError(null); };

  const handleSignOut = () => signOut(auth);

  if (loading) {
    return <Shell><div style={{ fontFamily: "'DM Sans',sans-serif", color: C.muted }}>Cargando…</div></Shell>;
  }

  if (!user) {
    const inputStyle = {
      width: "100%", padding: "12px 14px", borderRadius: "10px",
      border: `1.5px solid ${C.line}`, boxSizing: "border-box",
      fontFamily: "'DM Sans',sans-serif", fontSize: "14px", color: C.dark,
      outline: "none",
    };
    const primaryBtn = (busy) => ({
      width: "100%", padding: "12px 16px", border: "none", borderRadius: "10px",
      cursor: busy ? "default" : "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: 700,
      background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)", opacity: busy ? 0.7 : 1,
    });

    // Paso 2: ingresar el código recibido por email.
    if (step === "code") {
      return (
        <Shell>
          <div style={{ fontSize: "44px", marginBottom: "8px" }}>📬</div>
          <h1 style={{ fontSize: "22px", color: C.dark, margin: "0 0 8px" }}>Revisá tu correo</h1>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: C.muted, margin: "0 0 18px", lineHeight: 1.5 }}>
            Te enviamos un código de 6 dígitos a <strong style={{ color: C.dark }}>{emailKey(email)}</strong>.
          </p>
          <form onSubmit={handleVerifyCode}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••"
              style={{ ...inputStyle, textAlign: "center", fontSize: "24px", letterSpacing: "8px", marginBottom: "12px" }}
            />
            <button type="submit" disabled={verifying} style={primaryBtn(verifying)}>
              {verifying ? "Verificando…" : "Entrar"}
            </button>
          </form>
          {error && <div style={{ marginTop: "12px", padding: "8px", borderRadius: "6px", background: C.redBg, color: C.red, fontSize: "11px", fontFamily: "'DM Sans',sans-serif" }}>{error}</div>}
          <div style={{ marginTop: "16px", display: "flex", gap: "16px", justifyContent: "center" }}>
            <button onClick={handleSendCode} disabled={sending} style={{ background: "none", border: "none", cursor: "pointer", color: C.copperDark, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600 }}>
              {sending ? "Reenviando…" : "Reenviar código"}
            </button>
            <button onClick={resetLogin} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600 }}>
              Cambiar email
            </button>
          </div>
        </Shell>
      );
    }

    // Paso 1: email + Google.
    return (
      <Shell>
        <h1 style={{ fontSize: "24px", color: C.dark, margin: "0 0 8px" }}>Clase</h1>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: C.muted, margin: "0 0 24px" }}>
          Entrá con tu email o con tu cuenta de Google.
        </p>

        <form onSubmit={handleSendCode}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            autoComplete="email"
            style={{ ...inputStyle, marginBottom: "10px" }}
          />
          <button type="submit" disabled={sending} style={primaryBtn(sending)}>
            {sending ? "Enviando…" : "Enviarme un código"}
          </button>
        </form>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "18px 0" }}>
          <div style={{ flex: 1, height: "1px", background: C.line }} />
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "11px", color: C.muted }}>o</span>
          <div style={{ flex: 1, height: "1px", background: C.line }} />
        </div>

        <button onClick={handleSignIn} style={{
          width: "100%", padding: "12px 16px", borderRadius: "10px",
          cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: 700,
          background: "#fff", color: C.dark, border: `1.5px solid ${C.line}`,
        }}>Entrar con Google</button>

        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "11px", color: C.muted, margin: "16px 0 0", lineHeight: 1.5 }}>
          ¿No tenés cuenta de Google? Usá la opción de email: te mandamos un código y entrás sin contraseña.
        </p>

        {error && <div style={{ marginTop: "12px", padding: "8px", borderRadius: "6px", background: C.redBg, color: C.red, fontSize: "11px", fontFamily: "'DM Sans',sans-serif" }}>{error}</div>}
      </Shell>
    );
  }

  if (checking) {
    return <Shell><div style={{ fontFamily: "'DM Sans',sans-serif", color: C.muted }}>Verificando acceso…</div></Shell>;
  }

  if (!allowed) {
    return (
      <Shell>
        <div style={{ fontSize: "44px", marginBottom: "8px" }}>🔒</div>
        <h1 style={{ fontSize: "22px", color: C.dark, margin: "0 0 8px" }}>Acceso al curso</h1>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: C.muted, margin: "0 0 6px", lineHeight: 1.5 }}>
          Tu cuenta <strong style={{ color: C.dark }}>{user.email}</strong> todavía no tiene acceso al curso.
        </p>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, margin: "0 0 20px", lineHeight: 1.5 }}>
          Si ya compraste, asegurate de iniciar sesión con el mismo email.
        </p>
        {error && <div style={{ marginBottom: "12px", padding: "8px", borderRadius: "6px", background: C.redBg, color: C.red, fontSize: "11px", fontFamily: "'DM Sans',sans-serif" }}>{error}</div>}
        <a href={WOO_PRODUCT_URL} target="_blank" rel="noopener noreferrer" style={{
          display: "block", padding: "12px 16px", border: "none", borderRadius: "10px",
          textDecoration: "none", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: 700,
          background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: "10px",
        }}>Comprar el curso →</a>
        <button onClick={handleSignOut} style={{
          padding: "8px 14px", border: `1.5px solid ${C.line}`, borderRadius: "8px", cursor: "pointer",
          fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600,
          background: "#fff", color: C.muted,
        }}>Cerrar sesión</button>
      </Shell>
    );
  }

  return children({ user, signOut: handleSignOut });
}
